import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { ApiConfigService } from '../api/api-config.service';

/** Contrato real: GET /api/login/modules (hmg) → ModuloDTO[]. */
export interface ModuloDTO {
  id: number;
  acronym: string;
  name: string;
}

/**
 * Módulos habilitados para o tenant ativo. Deve ser recarregado após login
 * e a cada troca de tenant — ver AppShellComponent.
 */
@Injectable({ providedIn: 'root' })
export class ModuleService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiConfigService);

  private readonly _modules = signal<ModuloDTO[]>([]);
  private readonly _loaded = signal(false);

  readonly modules = this._modules.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly enabledAcronyms = computed(() => this._modules().map(m => m.acronym));

  load(): Observable<ModuloDTO[]> {
    return this.http.get<ModuloDTO[]>(this.api.url('/api/login/modules')).pipe(
      tap(modules => this.apply(modules ?? [])),
      catchError(() => {
        // Falha ao carregar módulos não deve travar o menu — cai no fallback
        // de isEnabled() (lista vazia = tudo habilitado, ver nota abaixo).
        this.apply([]);
        return of([]);
      })
    );
  }

  /**
   * Um módulo é considerado habilitado se o acronym veio na lista do
   * backend. DECISÃO: enquanto a lista estiver vazia (falha, tenant sem
   * módulos configurados, ou ainda não carregada), trata como "todos
   * habilitados" para não esconder o menu inteiro em ambientes onde o
   * backend de módulos ainda não está populado (ex.: dev/hmg iniciais).
   * Itens com rota placeholder continuam ocultos independentemente disso
   * (ver MenuService / PLACEHOLDER_ROUTES).
   */
  isEnabled(acronym: string): boolean {
    const acronyms = this.enabledAcronyms();
    if (acronyms.length === 0) return true;
    return acronyms.includes(acronym);
  }

  clear(): void {
    this._modules.set([]);
    this._loaded.set(false);
  }

  private apply(modules: ModuloDTO[]): void {
    this._modules.set(modules);
    this._loaded.set(true);
  }
}
